import React, { useState, useRef, useEffect } from 'react';
import { Block, SignatureBlockData, SignatureStamp, DistributionItem, SIGNATURE_BASE_HEIGHT_PT } from '../../../config';
import { SI_TOKENS, distributionTickInnerPt } from '../../../siChromeTokens';
import './signature-block.scss';

interface Engineer {
  id: string;
  first_name: string;
  last_name: string;
}

interface SignatureBlockProps {
  block: Block;
  onChange: (data: SignatureBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
  signatoryId?: string | null;
  signatoryName?: string;
  engineers?: Engineer[];
  onSignatoryChange?: (id: string) => void;
  // When the document has been signed, the stamp for its author slot: the
  // signature image plus the snapshotted title and registration. When absent
  // the block shows the name-over-a-line placeholder.
  stamp?: SignatureStamp | null;
}

const SignatureBlock: React.FC<SignatureBlockProps> = ({ 
  block, 
  onChange,
  isEditing,
  signatoryId,
  signatoryName,
  engineers = [],
  onSignatoryChange,
  stamp
}) => {
  const data = block.data as SignatureBlockData;
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
        setShowDropdown(false);
      }
    };

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFocused]);

  const handleClick = (e: React.MouseEvent) => {
    if (isEditing) {
      e.stopPropagation();
      setIsFocused(true);
    }
  };

  const distribution = data.distribution;

  const toggleDistribution = (itemId: string) => {
    if (!isEditing || !distribution) return;
    onChange({
      ...data,
      distribution: distribution.map(item =>
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    });
  };

  // On a site instruction the distribution list and the signature share one
  // line, as they do on the printed form. They are one block, not two, because
  // the editor measures a block to decide where the page breaks and the line is
  // only as tall as the taller column -- see SignatureBlockData.distribution.
  const renderDistribution = (items: DistributionItem[]) => (
    // Clicking the distribution column must not open the signatory bar: the two
    // columns are one block for measurement, not one control.
    <div className="signature-block__distribution" onClick={(e) => e.stopPropagation()}>
      <div className="signature-block__distribution-label">DISTRIBUTION</div>
      <div className="signature-block__distribution-items">
        {items.map(item => (
          <div key={item.id} className="distribution-item">
            <span className="distribution-item__label">{item.label}</span>
            <button
              type="button"
              className={`distribution-item__box${item.checked ? ' is-checked' : ''}`}
              aria-pressed={item.checked}
              aria-label={item.label}
              disabled={!isEditing}
              onClick={(e) => { e.stopPropagation(); toggleDistribution(item.id); }}
            >
              {/* Drawn, not typed: see SI_TOKENS.distributionTickPath. */}
              {item.checked && (
                <svg
                  width={`${distributionTickInnerPt}pt`}
                  height={`${distributionTickInnerPt}pt`}
                  viewBox={`0 0 ${distributionTickInnerPt} ${distributionTickInnerPt}`}
                  aria-hidden="true"
                >
                  <path
                    d={SI_TOKENS.distributionTickPath}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={SI_TOKENS.distributionTickStrokePt}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className={`signature-block${distribution ? ' signature-block--footer' : ''} ${isFocused ? 'focused' : ''}`}
      onClick={handleClick}
    >
      {/* Signature Action Bar - show when editing AND focused */}
      {isFocused && isEditing && (
        <div 
          className="signature-action-bar"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="toolbar-group">
            <span className="group-label">Signatory</span>
            <div className="dropdown-container">
              <button
                className="dropdown-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(!showDropdown);
                }}
              >
                {signatoryName || 'Select signatory...'}
                <span className="dropdown-arrow">▾</span>
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  {engineers.map(eng => (
                    <button
                      key={eng.id}
                      className={`dropdown-item ${signatoryId === eng.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSignatoryChange?.(eng.id);
                        setShowDropdown(false);
                      }}
                    >
                      {eng.first_name} {eng.last_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="signature-block__signature">
      {/* A site instruction has no closing. The line is omitted rather than
          rendered empty, and DocumentPDF omits it too -- see
          signatureSpaceLines(). */}
      {data.closing ? <div className="signature-closing">{data.closing}</div> : null}
      <div className="signature-company">{data.company}</div>
      <div className="signature-space">
        {stamp?.imageUrl && (
          <img
            className="signature-image"
            src={stamp.imageUrl}
            alt="Signature"
            // Inline because the values are per-signer data, not style.
            style={{
              left: `${stamp.offsetXMm ?? 0}mm`,
              bottom: `${-(stamp.offsetYMm ?? 0)}mm`,
              height: `${SIGNATURE_BASE_HEIGHT_PT * (stamp.scale ?? 1)}pt`,
            }}
          />
        )}
      </div>
      <div className="signature-line"></div>
      <div className="signatory-name">{stamp?.name || signatoryName || 'Select signatory...'}</div>
      {stamp?.title && <div className="signatory-meta">{stamp.title}</div>}
      {stamp?.registration && <div className="signatory-meta">{stamp.registration}</div>}
      </div>

      {distribution ? renderDistribution(distribution) : null}
    </div>
  );
};

export default SignatureBlock;
